import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { GlobalSearchResult, ProSearchData } from '../services/search.service';
import { useViewedPros } from '../contexts/ViewedProsContext';
import { masterproSearchPath } from '../utils/proSearch';

// Field-type highlighting (legacy "click a column to highlight it down the list").
// Each banded Field is keyed by its label; clicking any cell of a type toggles a
// bright highlight on every cell of that type across all (virtualized) cards, and
// hovering previews it in a softer tint. State lives in the provider (above the
// virtualized list) so it survives row recycling. Page-specific, never persisted.
interface FieldHighlightContextValue {
  enabled: boolean;
  lockedTypes: Set<string>;
  hoveredType: string | null;
  toggleType: (label: string) => void;
  setHoveredType: (label: string | null) => void;
}

const noop = () => {};
const FieldHighlightContext = createContext<FieldHighlightContextValue>({
  enabled: false,
  lockedTypes: new Set(),
  hoveredType: null,
  toggleType: noop,
  setHoveredType: noop,
});

export const FieldHighlightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lockedTypes, setLockedTypes] = useState<Set<string>>(() => new Set());
  const [hoveredType, setHoveredType] = useState<string | null>(null);

  const toggleType = useCallback((label: string) => {
    setLockedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const value = useMemo<FieldHighlightContextValue>(
    () => ({ enabled: true, lockedTypes, hoveredType, toggleType, setHoveredType }),
    [lockedTypes, hoveredType, toggleType],
  );

  return <FieldHighlightContext.Provider value={value}>{children}</FieldHighlightContext.Provider>;
};

// Banded field cell — small label above, value below; sits inside a grid track.
const SPAN_CLASS: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
};

export const Field: React.FC<{ label: string; value?: React.ReactNode; mono?: boolean; span?: number }> = ({
  label,
  value,
  mono = false,
  span = 1,
}) => {
  const { enabled, lockedTypes, hoveredType, toggleType, setHoveredType } = useContext(FieldHighlightContext);
  const isEmpty = value === null || value === undefined || value === '' || value === '0';
  const isLocked = enabled && lockedTypes.has(label);
  const isHovered = enabled && !isLocked && hoveredType === label;
  const highlightClass = isLocked
    ? 'bg-yellow-300 dark:bg-yellow-500/40'
    : isHovered
    ? 'bg-yellow-100 dark:bg-yellow-500/15'
    : '';

  return (
    <div
      className={`flex flex-col min-w-0 ${SPAN_CLASS[span] ?? 'col-span-1'} ${
        enabled ? 'cursor-pointer rounded px-1 -mx-1 transition-colors' : ''
      } ${highlightClass}`}
      onMouseEnter={enabled ? () => setHoveredType(label) : undefined}
      onMouseLeave={enabled ? () => setHoveredType(null) : undefined}
      onClick={enabled ? (e) => { e.stopPropagation(); toggleType(label); } : undefined}
      title={enabled ? `Click to highlight all "${label}" fields` : undefined}
    >
      <span className="text-[0.625rem] uppercase tracking-wide text-muted font-semibold leading-tight truncate">
        {label}
      </span>
      <span
        className={`text-xs text-gray-900 dark:text-gray-100 leading-snug truncate `}
        // Only a primitive makes a useful hover; a rendered element (an SF badge, a
        // formatted cell) would stringify to "[object Object]".
        title={!isEmpty && !enabled && (typeof value === 'string' || typeof value === 'number') ? String(value) : undefined}
      >
        {isEmpty ? <span className="text-gray-300 dark:text-gray-600">—</span> : value}
      </span>
    </div>
  );
};

// One banded field on the card: TanTable column id (SearchResults' proColumns),
// the Field label/mono/span, and how to read the value from the PRO payload.
interface CardFieldDef {
  id: string; // TanTable column id in SearchResults' proColumns
  label: string;
  mono?: boolean;
  span?: number;
  get: (d: ProSearchData) => React.ReactNode;
  /** Let the HOST's cell renderer win over `get` when `orderedFields` supplies a
   *  `render` (card mode inside TanTable). The catalog getter is authoritative for
   *  every other field on purpose — see `orderedFields` — but the carrier columns
   *  carry an interactive run-trace button that only the host column can build
   *  (it needs the pro, the trace side and the host's row-refresh callback), and a
   *  plain getter would silently drop it from the card. `get` stays as the
   *  fallback for hosts that pass no `render`. */
  hostRender?: boolean;
}

// The four bands, in render order. Tinted bands (1 and 3) get the alternating
// background; each band drops entirely when all of its fields are hidden.
const BANDS: { tinted: boolean; fields: CardFieldDef[] }[] = [
  {
    // Band 1 — People & Billing
    tinted: true,
    fields: [
      { id: 'csr', label: 'CSR', get: (d) => d.csrName || d.csr || null },
      { id: 'whseCsr', label: 'Whse CSR', get: (d) => d.whseCsrName || d.whseCsr || null },
      { id: 'dispatcher', label: 'Dispatcher', get: (d) => d.dispatcherName || d.dispatcher || null },
      { id: 'terminal', label: 'Terminal', hostRender: true, get: (d) => d.terminal },
      { id: 'customerName', label: 'Customer', get: (d) => d.customerName },
      { id: 'billToName', label: 'Bill To', get: (d) => d.billToName },
      { id: 'billFrom', label: 'Bill From', get: (d) => d.billFrom },
      { id: 'billToRef', label: 'Bill To Ref #', mono: true, get: (d) => d.billToRef },
      { id: 'invDate', label: 'Inv Date', get: (d) => d.invDate },
      { id: 'commodity', label: 'Commodity', get: (d) => d.commodity },
    ],
  },
  {
    // Band 2 — Equipment & Vessel
    tinted: false,
    fields: [
      { id: 'container', label: 'Equipment #', mono: true, get: (d) => d.container },
      { id: 'chassisId', label: 'Chass #', mono: true, get: (d) => d.chassisId },
      { id: 'size', label: 'Size', get: (d) => d.size },
      { id: 'equipType', label: 'Type', get: (d) => d.equipType },
      { id: 'isDrop', label: 'Drop', get: (d) => d.isDrop },
      { id: 'ssline', label: 'SSLine', mono: true, hostRender: true, get: (d) => d.ssline || null },
      { id: 'vessel', label: 'Vessel', get: (d) => d.vessel },
      { id: 'voyage', label: 'Voyage', mono: true, get: (d) => d.voyage },
      { id: 'po', label: 'PO', mono: true, get: (d) => d.po },
      { id: 'bol', label: 'BOL/BKG/Ref#', mono: true, get: (d) => d.bol },
    ],
  },
  {
    // Band 3 — Schedule & Cutoffs
    tinted: true,
    fields: [
      { id: 'available', label: 'Available', get: (d) => d.available },
      { id: 'lfd', label: 'LFD', get: (d) => d.lfd },
      { id: 'perDiem', label: 'Per Diem', get: (d) => d.perDiem },
      { id: 'puAppt', label: 'PU Appt', get: (d) => d.puAppt },
      { id: 'outGate', label: 'Out Gate', get: (d) => d.outGate },
      { id: 'docCut', label: 'Doc Cut', get: (d) => d.docCut },
      { id: 'cutoff', label: 'Cutoff', get: (d) => d.cutoff },
      { id: 'delivAppt', label: 'Deliv Appt', get: (d) => d.delivAppt },
      {
        id: 'delivTimes',
        label: 'Deliv Times',
        get: (d) => [d.delivApptTime1, d.delivApptTime2].filter(Boolean).join(' / ') || null,
      },
    ],
  },
  {
    // Band 4 — Locations / Drivers / Termination
    tinted: false,
    fields: [
      { id: 'pickupFrom', label: 'Pickup From', get: (d) => d.pickupFrom },
      { id: 'deliverTo', label: 'Deliver To', get: (d) => d.deliverTo },
      { id: 'returnTo', label: 'Return To', get: (d) => d.returnTo },
      {
        id: 'cityStateZip',
        label: 'City, State Zip',
        get: (d) =>
          [[d.deliverCity, d.deliverState].filter(Boolean).join(' '), d.deliverZip]
            .filter(Boolean)
            .join(', '),
      },
      { id: 'deliveryDriver', label: 'Driver (Del)', mono: true, get: (d) => d.deliveryDriver },
      { id: 'terminateDriver', label: 'Driver (Term)', mono: true, get: (d) => d.terminateDriver },
      { id: 'deliveryDate', label: 'Delivery Date', get: (d) => d.deliveryDate },
      { id: 'terminateDate', label: 'Terminate Date', get: (d) => d.terminateDate },
      { id: 'traceTerminate', label: 'Trace Terminate', get: (d) => d.traceTerminate },
      {
        id: 'comments',
        label: 'Comments',
        get: (d) => (d.comments ? d.comments.split('\n').filter(Boolean).join(' • ') : null),
      },
    ],
  },
];

// BANDS doubles as the field *catalog*: when the host drives order (card mode inside
// TanTable), position comes from column order and only label/mono/getter come from here.
const FIELD_CATALOG: Map<string, CardFieldDef> = new Map(
  BANDS.flatMap((b) => b.fields).map((f) => [f.id, f] as const),
);
const FIELDS_PER_ROW = 10; // matches the card's grid-cols-10

/** Every field the classic banded card renders — i.e. exactly what the pre-card-mode
 *  Multi Row View showed. Hosts use this to seed default column visibility so a fresh
 *  card view looks like the old one instead of the table's narrower core set. Columns
 *  with no catalog entry (e.g. the service-failure set) were never on the old card. */
export const PRO_CARD_FIELD_IDS: string[] = [...FIELD_CATALOG.keys()];

function chunk<U>(items: U[], size: number): U[][] {
  const out: U[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export interface ProResultCardProps {
  /** A 'pro'-type global search result whose `data` is a ProSearchData payload. */
  result: GlobalSearchResult;
  /** Called with the PRO number when the header link is clicked. */
  onProClick: (proNumber: string) => void;
  /** TanTable column ids currently visible. Omitted => show everything.
   *  Applies only to the classic banded layout — ignored when `orderedFields` is set. */
  visibleFields?: ReadonlySet<string>;
  /** User-added dynamic columns (additionalFields); values read from data[id].
   *  Applies only to the classic banded layout — ignored when `orderedFields` is set. */
  extraFields?: { id: string; label: string }[];
  /** Ordered visible columns from the host table. When provided, the card renders
   *  these fields in THIS order (10 per row) instead of the hand-arranged bands, so a
   *  column drag repositions the field. Ids without a catalog entry render with the
   *  supplied label and, when given, `render()` (the host column's own cell renderer
   *  — TanTable's ctx.renderCell — so a date field or an SF/TT badge looks as it does
   *  in the table), else the raw data value. Catalog ids ignore `render` and keep
   *  their band getter, except the few marked `hostRender` (the carrier columns,
   *  whose host cell carries the run-trace button). Omit for the classic banded layout —
   *  `visibleFields`/`extraFields` apply only to that path. */
  orderedFields?: { id: string; label: string; render?: () => React.ReactNode }[];
  /** Root element. 'li' (default) for <ul> hosts; 'div' inside a table cell. */
  as?: 'li' | 'div';
}

/**
 * Rich, banded PRO result card — the exact card used on the /searchresults page.
 * Extracted here so both Search Results and the LC dashboard render an identical view.
 */
const ProResultCard: React.FC<ProResultCardProps> = ({
  result,
  onProClick,
  visibleFields,
  extraFields,
  orderedFields,
  as,
}) => {
  const d = (result.data || {}) as ProSearchData;
  // Green "viewed" highlight once this PRO has been opened this session. Reads the
  // ViewedProsContext (no-ops to false when no provider is present, e.g. the LC
  // dashboard), mirroring the At Risk Monitor.
  const { isViewed } = useViewedPros();
  const viewed = isViewed(d.pronumber);
  // Soft-deleted order/template — red-highlight the whole card (deleted state wins
  // over the green "viewed" tint), matching the legacy search-results highlight.
  const deleted = d.deleted === 'Y';
  // CLOSED order (in ordersall, gone from orderscurrent). Labelled with a badge only —
  // no card tint or ring: on this page most rows ARE closed (Custom Filter and Custom
  // Search are permanently all-scope), so tinting them tinted the norm. Still gated on
  // !deleted so a deleted order shows one badge rather than two.
  const closed = !deleted && Boolean(d.isClosed);
  // Visibility gating: no set => show everything (LC dashboard path). Bands whose
  // fields are all hidden drop entirely; extras render as a fifth band.
  const show = (id: string) => !visibleFields || visibleFields.has(id);
  const bands = BANDS
    .map((b) => ({ ...b, fields: b.fields.filter((f) => show(f.id)) }))
    .filter((b) => b.fields.length > 0);
  const extras = (extraFields ?? []).filter((f) => show(f.id));
  // Host-driven order: the supplied columns fully determine the field set (the host
  // already filtered by visibility), so visibleFields/extraFields are not consulted.
  // Bands become positional striping — every other row tinted, matching bands 1 and 3.
  const orderedRows = orderedFields
    ? chunk(
        orderedFields.map((f): CardFieldDef => {
          const cat = FIELD_CATALOG.get(f.id);
          if (cat) {
            // Catalog ids keep their band getter, EXCEPT the few that opt into the
            // host's renderer (hostRender) and whose host actually supplied one.
            return cat.hostRender && f.render ? { ...cat, get: f.render } : cat;
          }
          return {
            id: f.id,
            label: f.label,
            get: f.render
              ? f.render
              : (data: ProSearchData) =>
                  (data as unknown as Record<string, React.ReactNode>)[f.id],
          };
        }),
        FIELDS_PER_ROW,
      )
    : null;
  const Root = as ?? 'li';

  return (
    <Root className={`m-3 rounded-xl overflow-hidden border shadow-sm ${
      deleted
        ? 'border-red-300 dark:border-red-800 ring-2 ring-red-300 dark:ring-red-800/60 bg-red-50 dark:bg-red-900/20'
        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40'
    }`}>
      {/* Header strip with clickable Pro #. Red when deleted, else green once viewed.
          Closed orders carry a badge but no tint — see `closed` above. */}
      <div
        className={`px-4 py-2 flex items-center gap-3 border-b transition-colors duration-150 ${
          deleted
            ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-800'
            : viewed
              ? 'bg-green-200 dark:bg-green-600/50 border-green-300 dark:border-green-700/50'
              : 'bg-purple-50/60 dark:bg-purple-900/10 border-purple-100 dark:border-purple-900/40'
        }`}
        title={deleted ? 'Deleted' : viewed ? 'Viewed' : undefined}
      >
        <button
          onClick={() => onProClick(String(d.pronumber))}
          className="text-base font-semibold text-purple-700 hover:text-purple-900 dark:text-purple-300 dark:hover:text-purple-200 underline underline-offset-2"
          title="Open order details"
        >
          {d.pronumber}
        </button>
        {deleted && (
          <span className="px-1.5 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-red-600 text-white">
            Deleted
          </span>
        )}
        {closed && (
          <span
            title="Closed order — no longer in the current orders tables"
            className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
          >
            Closed
          </span>
        )}
        {d.moveType && (
          <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200">
            {d.moveType}
          </span>
        )}
        {d.masterPro && d.masterPro !== d.pronumber && (
          <span className="text-xs text-muted">
            Master{' '}
            <a
              href={masterproSearchPath(d.masterPro)}
              target="_blank"
              rel="noopener noreferrer"
              title="All PROs on this Master PRO (new tab)"
              className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {d.masterPro}
            </a>
          </span>
        )}
        {d.isDrop === 'Y' && (
          <span className="px-1.5 py-0.5 text-[0.625rem] font-semibold rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
            DROP
          </span>
        )}
      </div>

      {/* Ordered path: fields flow in host column order, 10 per row, alternating
          rows tinted so the card keeps its banded look. */}
      {orderedRows ? orderedRows.map((fields, i) => {
        const isLast = i === orderedRows.length - 1;
        return (
          <div
            key={i}
            className={`px-4 py-2 grid grid-cols-10 gap-x-3 gap-y-2${
              isLast ? '' : ' border-b border-gray-200 dark:border-gray-700/60'
            }${i % 2 === 0 ? ' bg-white/70 dark:bg-gray-800/30' : ''}`}
          >
            {fields.map((f) => (
              <Field key={f.id} label={f.label} value={f.get(d)} mono={f.mono} span={f.span} />
            ))}
          </div>
        );
      }) : (<>

      {/* All bands share the same 10-column grid so cells line up vertically. The
          last rendered band carries no bottom border (matching the old band 4). */}
      {bands.map((band, i) => {
        const isLast = i === bands.length - 1 && extras.length === 0;
        return (
          <div
            key={i}
            className={`px-4 py-2 grid grid-cols-10 gap-x-3 gap-y-2${
              isLast ? '' : ' border-b border-gray-200 dark:border-gray-700/60'
            }${band.tinted ? ' bg-white/70 dark:bg-gray-800/30' : ''}`}
          >
            {band.fields.map((f) => (
              <Field key={f.id} label={f.label} value={f.get(d)} mono={f.mono} span={f.span} />
            ))}
          </div>
        );
      })}

      {/* Band 5 — Additional (user-added dynamic columns, values keyed by field id) */}
      {extras.length > 0 && (
        <div className="px-4 py-2 grid grid-cols-10 gap-x-3 gap-y-2">
          {extras.map((f) => (
            <Field key={f.id} label={f.label} value={(d as unknown as Record<string, React.ReactNode>)[f.id]} />
          ))}
        </div>
      )}
      </>)}
    </Root>
  );
};

export default ProResultCard;
